import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import history from 'routing/history';
import { H6 } from 'components/Typography';
import Button, { BackButton } from 'components/Button';
import ViewAnimator from '../../components/ViewAnimator';
import { StepHeading } from '../../components/StepHeading/styled';
import EditClaimError from '../../components/EditClaimError';
import useNavigateToNextStep from '../../hooks/useNavigateToNextStep';
import useNextAndPrevUrl from '../../hooks/useNextAndPrevUrl';
import {
  Buttons,
  PreExDescription,
  DescriptionContainer,
  Tips,
  TextArea
} from './styled';
import { setClaimInfo, editClaim, addValidStep, removeValidStep } from '../../state';
import { STEP_PATHS } from '../../consts';
import { NavigationButtons } from '../../styled';

const validation = Yup.object({
  isPreEx: Yup.boolean(),
  preExistingCondition: Yup.string().when('isPreEx', {
    is: true,
    then: Yup.string().required('Required')
  })
});

const PreEx = () => {
  const dispatch = useDispatch();
  const { preExistingCondition } = useSelector(
    (state) => state.nomadInsurance.claims.claimInfo
  );
  const { claimID, editError, isLoading } = useSelector(
    (state) => state.nomadInsurance.claims
  );
  const { claimType, step } = useParams();
  const { nextUrl, prevUrl } = useNextAndPrevUrl(claimType, step);
  useNavigateToNextStep(nextUrl, editError);

  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        isPreEx: Boolean(preExistingCondition),
        preExistingCondition
      }),
      [preExistingCondition] // eslint-disable-line
    ),
    onSubmit: async (info) => {
      const editedInfo = { preExistingCondition: info.preExistingCondition };
      if (claimID) {
        await dispatch(editClaim({ claimID, editedInfo }));
      } else {
        await dispatch(setClaimInfo(editedInfo));
        history.push(nextUrl);
      }
    },
    validateOnMount: true,
    validationSchema: validation
  });

  useEffect(() => {
    if ((formik.values.isPreEx && formik.isValid) || !formik.values.isPreEx) {
      dispatch(addValidStep(STEP_PATHS.PRE_EX));
    } else {
      dispatch(removeValidStep(STEP_PATHS.PRE_EX));
    }
  }, [formik.values.isPreEx, formik.isValid, dispatch]);

  return (
    <ViewAnimator>
      <StepHeading>
        Medical information <span>(2 of 3)</span>
      </StepHeading>
      <H6>
        Is the medical condition you are making a claim for connected to a past health
        issue?
      </H6>
      <Buttons>
        <Button
          variant="flat"
          onClick={() => formik.setFieldValue('isPreEx', true)}
          $selected={formik.values.isPreEx}
        >
          <b>Yes</b>
        </Button>
        <Button
          variant="flat"
          onClick={() => {
            formik.setFieldValue('isPreEx', false);
            formik.setFieldValue('preExistingCondition', '');
          }}
          $selected={!formik.values.isPreEx}
        >
          <b>No</b>
        </Button>
      </Buttons>
      {formik.values.isPreEx && (
        <PreExDescription>
          <b>Describe the health condition including how and when it began.</b>
          <DescriptionContainer>
            <Tips>
              <b>
                <span role="img" aria-label="tip">
                  💡
                </span>{' '}
                Tips
              </b>
              <ul>
                Please Include: <li>All symptoms and when they began</li>
                <li>
                  If you have ever had or been treated for this condition, describe when
                  and how
                </li>
              </ul>
            </Tips>
            <TextArea
              name="preExistingCondition"
              formik={formik}
              value={formik.values.preExistingCondition}
              placeholder="Write a detailed description here"
              variant="outline"
            />
          </DescriptionContainer>
        </PreExDescription>
      )}
      <EditClaimError isVisible={editError} />
      <NavigationButtons>
        <BackButton onClick={() => history.push(prevUrl)} variant="dark">
          Back
        </BackButton>
        <Button
          disabled={(formik.values.isPreEx && !formik.isValid) || isLoading}
          onClick={() => formik.submitForm()}
        >
          Save & next
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default PreEx;
